import React, { useEffect, useRef, useState } from "react";
import { Card, Spinner, Form, Row, Col } from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileDownload } from "@fortawesome/free-solid-svg-icons";
import { Button, ButtonGroup } from "@themesberg/react-bootstrap";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { fetchApi } from "../../../redux/api";
import { formatNumber } from "../../../redux/utils";
import moment from "moment";

const headerColors = [
  "#999", "#ccc", "#eee", "#f2f2f2", "#f5f5f5", "#f9f9f9",
];

function unflattenTable2(arr) {
  var tree = [],
    mappedArr = {},
    arrElem, mappedElem;

  for (var i = 0, len = arr?.length; i < len; i++) {
    arrElem = arr[i];
    if (arrElem?.head) {
      mappedArr[arrElem.head] = { ...arrElem, parentHeads: [], children: [] };
    }
  }

  for (i = 0; i < len; i++) {
    arrElem = arr[i];
    mappedElem = mappedArr[arrElem.head];

    if (mappedElem) {
      const parentHeads = [];
      let currentParent = mappedElem;
      while (currentParent?.sub_head) {
        parentHeads.unshift(currentParent?.sub_head);
        currentParent = mappedArr[currentParent?.sub_head];
      }
      mappedElem.parentHeads = parentHeads;

      if (mappedElem?.sub_head) {
        if (mappedArr[mappedElem.sub_head]) {
          mappedArr[mappedElem.sub_head].children.push(mappedElem);
        } else {
          tree.push(mappedElem);
        }
      } else {
        tree.push(mappedElem);
      }
    }
  }

  return tree;
}

function getLikeCode(code) {
  if (!code) return null;
  if (code === "Personnel") return "21%";
  if (code === "OverHead") return "22%";
  if (code === "Capital") return "32%";
  return code;
}

function mergeDataWithHead(getHead, data) {
  const merged = {};
  if (getHead) getHead.forEach((item) => {
    merged[item.head] = { ...item };
  });
  if (data) data.forEach((item) => {
    if (merged[item.head]) {
      merged[item.head] = { ...merged[item.head], ...item };
    } else {
      merged[item.head] = { ...item };
    }
  });
  return Object.values(merged);
}

const isEmpty = (v) =>
  v === 0 || v === null || v === undefined || v === "--" || v === "0.00" || v === "";

export default function ReportExpByMDA({
  defaultType = "total_ex_by_mda",
  defaultTitle = "Total Expenditure by MDA",
  options = [],
  route = "reports",
  sheetName = "Expenditure by MDA",
  chartType = "get_head_eco",
}) {
  const [rawData, setRawData] = useState([]);
  const [getHead, setGetHead] = useState([]);
  const [form, setForm] = useState({
    budget_year: moment().format("YYYY"),
  });
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(
    options[0] || { value: defaultType, title: defaultTitle, code: null }
  );
  const years = Array.from({ length: 10 }, (_, i) =>
    moment().format("YYYY") - i
  );
  const tableRef = useRef(null);

  const handleChange = ({ target: { name, value } }) => {
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleButtonClick = (item) => {
    setSelectedOption(item);
  };

  useEffect(() => {
    fetchApi(`${route}?query_type=${chartType}&budget_year=${form.budget_year}`)
      .then((data) => {
        setGetHead(data?.results || []);
      })
      .catch((err) => console.log(err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoading(true);
    const likeCode = getLikeCode(selectedOption.code);
    const url = `${route}?query_type=${selectedOption.value}&budget_year=${form.budget_year}${likeCode ? `&like=${likeCode}` : ""}`;
    fetchApi(url)
      .then((data) => {
        setRawData(data?.results || []);
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false));
  }, [form, selectedOption]);

  const ensureNumber = (value) =>
    isNaN(value) || value === null || value === undefined ? 0 : Number(value);

  const calcSum = (node, key) => {
    if (!node.children || node.children.length === 0) {
      return ensureNumber(node[key]);
    }
    let sum = ensureNumber(node[key]);
    for (const child of node.children) {
      sum += calcSum(child, key);
    }
    return sum;
  };

  const allKeys = ["prev_year2_actual_amount", "prev_year1_approve_amount", "prev_year1_actual_amount", "budget_amount", "approve_amount"];

  const newFilter = (item) =>
    allKeys.every((k) => isEmpty(item[k]));

  const newFilterHead = (item) =>
    allKeys.every((k) => isEmpty(calcSum(item, k)));

  const dataByMDA = {};
  if (rawData) rawData.forEach((item) => {
    const mda = item.mda_code || "unknown";
    if (dataByMDA[mda]) {
      dataByMDA[mda].push(item);
    } else {
      dataByMDA[mda] = [item];
    }
  });

  const grandTotalPrevYear2 = rawData.reduce((s, i) => s + ensureNumber(i.prev_year2_actual_amount), 0);
  const grandTotalPrevYear1App = rawData.reduce((s, i) => s + ensureNumber(i.prev_year1_approve_amount), 0);
  const grandTotalPrevYear1Act = rawData.reduce((s, i) => s + ensureNumber(i.prev_year1_actual_amount), 0);
  const grandTotalBudget = rawData.reduce((s, i) => s + ensureNumber(i.budget_amount), 0);
  const grandTotalApprove = rawData.reduce((s, i) => s + ensureNumber(i.approve_amount), 0);

  const getMdaName = (mdaCode) => {
    const item = rawData?.find((d) => d.mda_code === mdaCode);
    return item?.mda_name || "";
  };

  const renderItems = (item, index) => (
    <React.Fragment key={`group_${index}`}>
      {!newFilterHead(item) && (
        <tr
          style={{
            fontSize: "12px",
            fontWeight: "bold",
            color: "black",
            cursor: "pointer",
          }}
        >
          <td
            style={{
              backgroundColor: headerColors[item.parentHeads?.length] || "",
              color: "black",
            }}
          >
            {item.head}
          </td>
          <td
            style={{
              backgroundColor: headerColors[item.parentHeads?.length] || "",
              color: "black",
            }}
          >
            {item.description || ""}
          </td>
          {allKeys.map((k) => (
            <td
              key={k}
              style={{
                textAlign: "right",
                backgroundColor: headerColors[item.parentHeads?.length] || "",
                color: "black",
              }}
            >
              {formatNumber(calcSum(item, k))}
            </td>
          ))}
        </tr>
      )}
      {item.children?.map((child, idx) => {
        if (child.children?.length) {
          return renderItems(child, idx);
        }
        if (newFilter(child)) return null;
        return (
          <tr key={child.head || idx} style={{ fontSize: "11px" }}>
            <td>{child.head}</td>
            <td>{child.description || ""}</td>
            {allKeys.map((k) => (
              <td key={k} style={{ textAlign: "right" }}>
                {formatNumber(child[k])}
              </td>
            ))}
          </tr>
        );
      })}
    </React.Fragment>
  );

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>{selectedOption.title}</h4>
        </div>
      </div>

      <Card border="light" className="shadow-sm">
        <Card.Body>
          <Row className="mb-3">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Budget Year</Form.Label>
                <Form.Select
                  name="budget_year"
                  value={form.budget_year}
                  onChange={handleChange}
                >
                  <option>--select--</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6} className="text-center">
              <ButtonGroup>
                {options.map((item) => (
                  <Button
                    size="sm"
                    key={item.title}
                    variant={
                      selectedOption.title === item.title
                        ? "primary"
                        : "outline-secondary"
                    }
                    onClick={() => handleButtonClick(item)}
                  >
                    <small>{item.title}</small>
                  </Button>
                ))}
              </ButtonGroup>
            </Col>
            <Col
              md={3}
              className="d-flex align-items-end justify-content-end"
            >
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  if (tableRef.current) {
                    const ws = XLSX.utils.table_to_sheet(tableRef.current);
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, ws, sheetName);
                    const buf = XLSX.write(wb, {
                      bookType: "xlsx",
                      type: "array",
                    });
                    FileSaver.saveAs(
                      new Blob([buf], {
                        type: "application/octet-stream",
                      }),
                      `${selectedOption.title}.xlsx`
                    );
                  }
                }}
              >
                <FontAwesomeIcon icon={faFileDownload} className="me-1" />{" "}
                Download Excel
              </Button>
            </Col>
          </Row>

          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <div className="table-responsive">
              <table
                className="table table-bordered"
                ref={tableRef}
                style={{ fontSize: "13px" }}
              >
                <thead>
                  <tr
                    style={{
                      fontSize: "12px",
                      textAlign: "center",
                      backgroundColor: "orange",
                    }}
                  >
                    <th style={{ backgroundColor: "orange" }}>Code</th>
                    <th style={{ backgroundColor: "orange" }}>Description</th>
                    <th style={{ backgroundColor: "orange" }}>{`${
                      parseInt(form.budget_year) - 2
                    } Full Year Actuals`}</th>
                    <th style={{ backgroundColor: "orange" }}>{`${
                      parseInt(form.budget_year) - 1
                    } Approved`}</th>
                    <th style={{ backgroundColor: "orange" }}>{`${
                      parseInt(form.budget_year) - 1
                    } Performance Jan. to Aug.`}</th>
                    <th style={{ backgroundColor: "orange" }}>{`${parseInt(
                      form.budget_year
                    )} MDA Proposal`}</th>
                    <th style={{ backgroundColor: "orange" }}>{`${parseInt(
                      form.budget_year
                    )} Approved Budget`}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    style={{
                      fontWeight: "bold",
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <td colSpan={2} style={{ fontWeight: "bold", textAlign: "left" }}>
                      GRAND TOTAL
                    </td>
                    <td style={{ textAlign: "right", fontWeight: "bold", backgroundColor: "#C0C0C0" }}>
                      {formatNumber(grandTotalPrevYear2)}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: "bold", backgroundColor: "#C0C0C0" }}>
                      {formatNumber(grandTotalPrevYear1App)}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: "bold", backgroundColor: "#C0C0C0" }}>
                      {formatNumber(grandTotalPrevYear1Act)}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: "bold", backgroundColor: "#C0C0C0" }}>
                      {formatNumber(grandTotalBudget)}
                    </td>
                    <td style={{ textAlign: "right", fontWeight: "bold", backgroundColor: "#C0C0C0" }}>
                      {formatNumber(grandTotalApprove)}
                    </td>
                  </tr>
                  {Object.keys(dataByMDA).map((mdaCode, grpIdx) => {
                    const mdaItems = dataByMDA[mdaCode];
                    const mdaTotalPrev2 = mdaItems.reduce((s, i) => s + ensureNumber(i.prev_year2_actual_amount), 0);
                    const mdaTotalPrev1App = mdaItems.reduce((s, i) => s + ensureNumber(i.prev_year1_approve_amount), 0);
                    const mdaTotalPrev1Act = mdaItems.reduce((s, i) => s + ensureNumber(i.prev_year1_actual_amount), 0);
                    const mdaTotalBudget = mdaItems.reduce((s, i) => s + ensureNumber(i.budget_amount), 0);
                    const mdaTotalApprove = mdaItems.reduce((s, i) => s + ensureNumber(i.approve_amount), 0);
                    const isAllZero = mdaTotalPrev2 === 0 && mdaTotalPrev1App === 0 && mdaTotalPrev1Act === 0 && mdaTotalBudget === 0 && mdaTotalApprove === 0;
                    if (isAllZero) return null;
                    const merged = mergeDataWithHead(getHead, mdaItems);
                    const tt = unflattenTable2(merged);
                    return (
                      <React.Fragment key={grpIdx}>
                        <tr>
                          <td
                            colSpan={7}
                            style={{
                              fontWeight: "bold",
                              backgroundColor: "green",
                              color: "white",
                            }}
                          >
                            {mdaCode} - {getMdaName(mdaCode)}
                          </td>
                        </tr>
                        {tt?.map((item, i) => renderItems(item, i))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );
}
